import React, { createContext, useState, useMemo, useEffect, useContext, useCallback } from 'react';
import { useIsAuthenticated, AuthContextProvider } from 'shared/utils/auth'
import { requestUserData, updateUserData, UserDataResponseT } from './api';

export type UserAuthContextUserT = UserDataResponseT;

export type UserAuthContextValueT = {
  user: UserAuthContextUserT | null,
  updateUserData: (fields: Parameters<typeof updateUserData>[0]) => Promise<void>
  loadingUser: boolean,
  updatingUser: boolean,
  isAuthenticated: boolean
}

const UserAuthContext = createContext<null | UserAuthContextValueT>(null);

export function UserAuthContextProvider({ children }) {
  return (
    <AuthContextProvider>
      <UserAuthContextProviderInternal>
        {children}
      </UserAuthContextProviderInternal>
    </AuthContextProvider>
  )
}

function UserAuthContextProviderInternal({ children }) {
  const [user, setUser] = useState<UserDataResponseT | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const loadUserData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await requestUserData();
      setUser(data);
      setLoading(false);
    } catch (e) {
      throw e;
    }
  }, []);

  const requestUpdateUserData = useCallback(async (fields: Parameters<typeof updateUserData>[0]) => {
    setUpdating(true);
    try {
      const response = await updateUserData(fields);
      setUser(user => user ? {...user, ...response} : response);
      setUpdating(false);
    } catch (e) {
      setUpdating(false);
      throw e;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return setUser(null);
    loadUserData();
  }, [isAuthenticated]);

  const contextValue = useMemo(() => {
    return {
      user,
      loadingUser: loading,
      updatingUser: updating,
      isAuthenticated,
      updateUserData: requestUpdateUserData
    }
  }, [user, loading, updating, isAuthenticated, requestUpdateUserData]);

  return (
    <UserAuthContext.Provider value={contextValue}>
      {children}
    </UserAuthContext.Provider>
  )
}

export function useAuthenticatedUserData () {
  const value = useContext(UserAuthContext);
  if (!value) throw new Error('UserAuthContextProvider is not initialized');
  return value;
}
